import { useState, useEffect, useRef } from 'react';
import Overlay from '../Overlay';
import InlineMessage from '../InlineMessage';
import useMessage from '../../lib/useMessage';
import {
  parseCsv,
  findEncodingDamage,
  planSummary,
  rejectedRowsCsv,
  readWorkbookSheets,
  isWorkbookFile,
  IMPORT_HARD_CAP,
} from '../../lib/importCore';
import { importTypesFor, buildTemplateFile } from '../../lib/importDescriptors';
import { CREATE_UNIT } from '../../lib/vendorItemsImport';

// Issues #194 and #422, Sessions 78n10 and 78s.1. The one import flow.
//
// In plain terms: pick what you are importing, choose a file, answer anything
// Stede could not work out, read four numbers, then press the button. Nothing
// is written before those four numbers have been shown.
//
// Approved gate: docs/gates/Stede_MasterDataImport_Gate_422.html, decision M22.
// THIS COMPONENT NEVER BRANCHES ON WHAT IS BEING IMPORTED. Every difference
// between a vendor list and a vendor's price list is read out of a descriptor
// (lib/importDescriptors.js). The test of that claim is the item-catalog
// importer at Session 78t: if it needs one edit in this file, the split was
// drawn in the wrong place.
//
// RENAMED from VendorCatalogImportSheet.js at 78s.1, because it no longer
// imports only the vendor catalog and a name that lies about scope is how the
// next person concludes there must be a second screen somewhere.
//
// Five steps: pick → choose → map (only when a column was not understood) →
// ask (only when a VALUE was not recognized) → PREVIEW → result. Two of the
// five are skipped on a clean file, which is the common case.

const TONE = {
  create: { bar: '#1D9E75', wash: 'transparent' },
  update: { bar: '#3C7FD0', wash: 'transparent' },
  unchanged: { bar: '#D8D8D4', wash: 'transparent' },
  warn: { bar: '#BA7517', wash: '#FDF6E7' },
  error: { bar: '#A32D2D', wash: '#FDF3F3' },
};

// #466. Shown when the decoded file contains U+FFFD. It is a warning and not a
// stop - see findEncodingDamage() for why. Placed above the preview so it is
// read BEFORE the four numbers, since the fix is to re-save the file rather
// than to correct anything on this screen.
function EncodingWarning({ damage }) {
  if (!damage) return null;
  return (
    <div style={{
      padding: '11px 13px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.55,
      background: TONE.warn.wash, border: '1px solid #F0C040', color: '#633806',
      marginBottom: 10,
    }}>
      <strong>
        {damage.count === 1
          ? 'One character did not come through.'
          : damage.count.toLocaleString() + ' characters did not come through.'}
      </strong>{' '}
      This happens when a file is saved as <strong>CSV (Comma delimited)</strong> or
      {' '}<strong>CSV (MS-DOS)</strong> and something in it is not a plain English
      letter - an accent, a curly apostrophe, a dash Excel corrected for you.
      <div style={{
        marginTop: 7, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12, wordBreak: 'break-all',
      }}>
        {damage.sample}
      </div>
      <div style={{ marginTop: 7 }}>
        You can carry on - everything else in the file is fine and a name can be
        fixed here afterwards. To fix it properly, re-save the spreadsheet as
        {' '}<strong>CSV UTF-8 (Comma delimited)</strong> and choose the file again.
      </div>
    </div>
  );
}

function StatTiles({ s }) {
  const tiles = [
    { label: 'New', value: s.create, color: '#1D9E75' },
    { label: 'Updated', value: s.update, color: '#3C7FD0' },
    { label: 'Unchanged', value: s.unchanged, color: 'var(--text-primary, #0F0F0F)' },
    { label: 'Cannot import', value: s.error, color: s.error > 0 ? '#A32D2D' : 'var(--text-primary, #0F0F0F)' },
  ];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
      background: 'var(--gray-ln, #E3E3DF)', border: '1px solid var(--gray-ln, #E3E3DF)',
      borderRadius: 8, overflow: 'hidden', marginBottom: 10,
    }}>
      {tiles.map(t => (
        <div key={t.label} style={{ background: '#fff', padding: '9px 6px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: t.color, lineHeight: 1.1 }}>{t.value}</div>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 2 }}>{t.label}</div>
        </div>
      ))}
    </div>
  );
}

// The detail line is the descriptor's sentence, not this component's. That is
// the difference between a shared preview and a preview that quietly knows
// about vendors.
function PlanRow({ entry, detail }) {
  const tone = entry.mode === 'error' ? TONE.error
    : entry.warnings.length ? TONE.warn
    : TONE[entry.mode] || TONE.unchanged;

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 8,
      padding: '8px 10px', marginBottom: 6, borderRadius: 7,
      border: '1px solid var(--gray-ln, #E3E3DF)', borderLeft: `3px solid ${tone.bar}`,
      background: tone.wash === 'transparent' ? '#fff' : tone.wash,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {entry.displayName || '(no name)'}
        </div>
        <div style={{ fontSize: 11.5, color: '#5A5A5A', marginTop: 2, lineHeight: 1.45 }}>
          {detail}
        </div>
        {entry.warnings.map((w, i) => (
          <div key={i} style={{ fontSize: 11.5, color: '#633806', marginTop: 3, lineHeight: 1.45 }}>{w}</div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#9A9A9A', whiteSpace: 'nowrap' }}>Row {entry.lineNo}</div>
    </div>
  );
}

// M1. The choice at the top of the flow, filtered by permission (M15) — so
// somebody who may load a price list but may not decide what D&R stocks sees
// one card rather than a disabled one. A disabled control invites a question
// nobody can answer from the screen.
function TypeCard({ descriptor, onPick }) {
  return (
    <button
      onClick={() => onPick(descriptor)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        border: '1px solid var(--gray-ln, #E3E3DF)', borderRadius: 9,
        background: '#fff', padding: '13px 14px', marginBottom: 8,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary, #0F0F0F)' }}>
        {descriptor.label}
      </div>
      <div style={{ fontSize: 12.5, color: '#5A5A5A', marginTop: 3, lineHeight: 1.5 }}>
        {descriptor.what}
      </div>
    </button>
  );
}

// #468 layer 2, M7 and M9. One question per distinct unrecognized value, never
// one per row — "12 rows say sq ft" rather than twelve rejections. M9's half is
// the option list: where a value could mean more than one thing, all of them are
// offered rather than the first being taken silently.
function ValueQuestion({ question, options, answer, canCreate, onAnswer }) {
  return (
    <div style={{
      padding: '11px 13px', marginBottom: 8, borderRadius: 8,
      border: '1px solid var(--gray-ln, #E3E3DF)', borderLeft: '3px solid #BA7517',
      background: '#FDF6E7',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#633806' }}>
        {question.count === 1
          ? `One row says “${question.value}”.`
          : `${question.count} rows say “${question.value}”.`}
      </div>
      <div style={{ fontSize: 12, color: '#633806', marginTop: 3, lineHeight: 1.5 }}>
        That is not one of our {question.label.toLowerCase()}s. What should it be?
        {' '}<span style={{ opacity: 0.8 }}>
          Row{question.lines.length === 1 ? ' ' : 's '}{question.lines.slice(0, 6).join(', ')}
          {question.lines.length > 6 ? ` and ${question.lines.length - 6} more` : ''}.
        </span>
      </div>
      <select
        value={answer ?? ''}
        onChange={e => onAnswer(e.target.value)}
        style={{
          marginTop: 9, width: '100%', fontSize: 16, padding: '8px 9px',
          borderRadius: 6, border: '1px solid var(--gray-ln, #E3E3DF)', background: '#fff',
        }}
      >
        <option value="">Choose…</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        {/* M8. A new package type may be added during an import; a new category
            may not. The descriptor decides which, so this component does not
            have to know the difference. */}
        {canCreate && <option value={CREATE_UNIT}>Add “{question.value}” as a new {question.label.toLowerCase()}</option>}
        <option value="__blank__">Leave it blank on those rows</option>
      </select>
    </div>
  );
}

export default function MasterDataImportSheet({ isMobile, onClose, onImported, auth }) {
  const available = importTypesFor(auth);

  const [descriptor, setDescriptor] = useState(available.length === 1 ? available[0] : null);
  const [step, setStep] = useState(available.length === 1 ? 'choose' : 'pick');
  const [loadingRef, setLoadingRef] = useState(false);
  const [reference, setReference] = useState(null);
  const [buildingTemplate, setBuildingTemplate] = useState(false);

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState(null);
  const [headerRowIndex, setHeaderRowIndex] = useState(-1);
  const [headerInfo, setHeaderInfo] = useState(null);
  const [manualMap, setManualMap] = useState({});   // fileColumnIndex -> our key

  const [questions, setQuestions] = useState([]);
  const [valueMap, setValueMap] = useState({});     // lowercased file value -> our value
  const [mergedMapping, setMergedMapping] = useState(null);

  const [plan, setPlan] = useState([]);
  const [filter, setFilter] = useState('all');
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [encodingDamage, setEncodingDamage] = useState(null);   // #466

  const fileRef = useRef(null);
  const msg = useMessage();

  // The reference data every plan is diffed against — loaded per descriptor
  // rather than once, because the two imports read different tables. Loading
  // both up front would query four tables to import a vendor list.
  useEffect(() => {
    if (!descriptor) return;
    let cancelled = false;
    setLoadingRef(true);
    (async () => {
      try {
        const ref = await descriptor.loadReference();
        if (!cancelled) setReference(ref);
      } catch (e) {
        if (!cancelled) msg.showError('Could not read what Stede already has: ' + (e.message || e));
      } finally {
        if (!cancelled) setLoadingRef(false);
      }
    })();
    return () => { cancelled = true; };
  }, [descriptor]); // eslint-disable-line

  function pickType(d) {
    setDescriptor(d);
    setStep('choose');
    msg.clearMessage();
  }

  // Session 78u, gate decision M14. A workbook and a CSV converge here: both
  // become the same array of rows, and everything after this point cannot tell
  // which one it came from.
  //
  // THE RIGHT SHEET IS FOUND BY READING IT, not by its name or its position.
  // The workbook carries three tabs and only one has the headings on it, so the
  // How-To and Lists sheets are skipped without a rule naming them - and
  // renaming a tab breaks nothing. Same content-matching rule findHeaderRow
  // already applies to rows, applied to sheets.
  // THE BEST-MATCHING SHEET, NOT THE FIRST MATCHING ONE, and the difference is
  // a defect the round-trip test caught before this shipped.
  //
  // "Take the first sheet whose headings are findable" reads as obviously
  // right and is wrong on our own workbook: the How-To sheet carries a table
  // called "What each column means" which LISTS THE COLUMN NAMES DOWN A COLUMN,
  // so findHeaderRow matches it too - and it comes first. The importer would
  // have read the instructions page as the data, with two columns unmatched and
  // the help text as rows.
  //
  // Scoring on how well a sheet matches settles it without naming any sheet,
  // so renaming a tab still breaks nothing and a supplier's own single-sheet
  // workbook is unaffected.
  function pickDataSheet(sheets) {
    let best = { parsed: sheets[0] ? sheets[0].rows : [], hIdx: -1, score: -1 };
    sheets.forEach(sheet => {
      const hIdx = descriptor.findHeaderRow(sheet.rows);
      if (hIdx === -1) return;
      const info = descriptor.mapHeaders(sheet.rows[hIdx]);
      const score = Object.keys(info.mapping).length - info.unmatched.length;
      if (score > best.score) best = { parsed: sheet.rows, hIdx, score };
    });
    return best;
  }

  async function readWorkbook(file) {
    return pickDataSheet(await readWorkbookSheets(await file.arrayBuffer()));
  }

  function readFile(file) {
    if (!file) return;
    setFileName(file.name);
    setEncodingDamage(null);
    msg.clearMessage();

    if (isWorkbookFile(file)) {
      readWorkbook(file)
        .then(({ parsed, hIdx }) => acceptRows(parsed, hIdx))
        .catch(e => msg.showError('That workbook could not be read: ' + (e.message || e)));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = String(reader.result || '');
        // #466. Checked on the raw decoded text, before parsing, because a
        // mangled character can land in a heading as easily as in a name.
        // A workbook needs none of this - there is no encoding to get wrong,
        // which is half the reason M14 exists.
        setEncodingDamage(findEncodingDamage(raw));
        acceptRows(parseCsv(raw));
      } catch (e) {
        msg.showError('That file could not be read: ' + (e.message || e));
      }
    };
    reader.onerror = () => msg.showError('That file could not be read.');
    reader.readAsText(file);
  }

  // Everything a file has to satisfy once it is rows, whichever kind it was.
  // Extracted at 78u rather than duplicated for the workbook path: a second
  // copy of the row cap, the empty-file check and the mapping decision is a
  // second place for them to drift, and locked rule 14 is about exactly this -
  // the duplication worth removing is the sequence, not the shape.
  function acceptRows(parsed, knownHeaderRow) {
    const hIdx = knownHeaderRow === undefined
      ? descriptor.findHeaderRow(parsed)
      : knownHeaderRow;
    if (hIdx === -1) {
      msg.showError('No column headings were found in that file. The first row should name the columns — download the blank template to see the format.');
      return;
    }
    const dataRows = parsed.length - hIdx - 1;
    if (dataRows > IMPORT_HARD_CAP) {
      msg.showError(`That file has ${dataRows.toLocaleString()} rows — more than the ${IMPORT_HARD_CAP.toLocaleString()} one import can carry. Split it and import each part.`);
      return;
    }
    if (dataRows < 1) {
      msg.showError('That file has column headings but no rows under them.');
      return;
    }
    const info = descriptor.mapHeaders(parsed[hIdx]);
    setRows(parsed);
    setHeaderRowIndex(hIdx);
    setHeaderInfo(info);
    setManualMap({});

    // D6. The mapping step exists to ask about columns nobody understood.
    // When there are none, it is skipped — but the file still has to carry
    // what this import cannot do without, and that is the descriptor's
    // judgment rather than this component's.
    if (info.unmatched.length === 0) {
      settle(parsed, hIdx, info.mapping);
    } else {
      setStep('map');
    }
  }

  // Everything between "the columns are settled" and "show the preview": check
  // the file carries what this import needs, then ask about any VALUE that is
  // not one of ours.
  function settle(theRows, hIdx, mapping) {
    const found = new Set(Object.values(mapping));
    const blocker = descriptor.mappingBlocker(found);
    if (blocker) { msg.showError(blocker); return; }

    const qs = descriptor.collectQuestions({
      rows: theRows, headerRowIndex: hIdx, mapping, reference,
    });
    if (qs.length) {
      setQuestions(qs);
      setValueMap({});
      setStep('ask');
      return;
    }
    setQuestions([]);
    goPreview(theRows, hIdx, mapping, {});
  }

  function goPreview(theRows, hIdx, mapping, answers) {
    const built = descriptor.buildPlan({
      rows: theRows, headerRowIndex: hIdx, mapping, reference, valueMap: answers,
    });
    setPlan(built);
    setFilter('all');
    setStep('preview');
  }

  function continueFromMapping() {
    const merged = { ...headerInfo.mapping };
    for (const [idx, key] of Object.entries(manualMap)) {
      if (key) merged[Number(idx)] = key;
    }
    setMergedMapping(merged);
    settle(rows, headerRowIndex, merged);
  }

  // Held so the ask step can rebuild the plan without re-merging the manual
  // choices a second time.
  const activeMapping = mergedMapping || headerInfo?.mapping || {};

  function answerQuestion(question, value) {
    // '__blank__' is a real answer meaning "no value on those rows", which is
    // different from not having answered. An empty string is the unanswered
    // state, so the two cannot be confused.
    const stored = value === '__blank__' ? '' : value;
    setValueMap(m => ({ ...m, [question.value.trim().toLowerCase()]: stored }));
  }

  const answeredAll = questions.every(q => valueMap[q.value.trim().toLowerCase()] !== undefined);

  async function doImport() {
    setWorking(true);
    setProgress({ done: 0, total: summary.willWrite });
    msg.clearMessage();
    try {
      const r = await descriptor.execute(
        plan,
        { reference, questions, valueMap },
        (done, total) => setProgress({ done, total }),
      );
      if (r.fatal) { msg.showError(r.fatal); return; }
      setResult(r);
      setStep('result');
      if (onImported) await onImported();
    } catch (e) {
      msg.showError('The import stopped: ' + (e.message || e));
    } finally {
      setWorking(false);
      setProgress(null);
    }
  }

  // #465. A blank template, GENERATED from the descriptor's column list rather
  // than served as a file under public/. That distinction is the whole point: a
  // static CSV is a second copy of the column list, and the moment a column is
  // added the two disagree with nothing to catch it. #465 is that failure
  // already having happened once -- an Aug 19 template with 6 columns against
  // this importer's 16, sitting where it looked official.
  // Session 78u. Which KIND of template this is belongs to the descriptor, not
  // here - the two master-data imports hand back a .xlsx workbook and the
  // vendor list still hands back its CSV, and this function cannot tell.
  async function downloadBlankTemplate() {
    setBuildingTemplate(true);
    try {
      const { blob, name } = await buildTemplateFile(descriptor, reference);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      msg.showError('That template could not be built: ' + (e.message || e));
    } finally {
      setBuildingTemplate(false);
    }
  }

  function downloadRejects() {
    const csv = rejectedRowsCsv(rows, headerRowIndex, plan);
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.replace(/\.csv$/i, '') + '_skipped_rows.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const summary = planSummary(plan);
  const shown = plan.filter(e => {
    if (filter === 'all') return true;
    if (filter === 'problems') return e.mode === 'error' || e.warnings.length > 0;
    return e.mode === filter;
  });

  const usedKeys = new Set([
    ...Object.values(headerInfo?.mapping || {}),
    ...Object.values(manualMap).filter(Boolean),
  ]);

  const noun = descriptor?.noun || { one: 'row', many: 'rows' };
  const banner = descriptor?.banner ? descriptor.banner(headerInfo) : null;

  const title = step === 'result' ? 'Import complete'
    : step === 'map' ? 'Match the columns'
    : step === 'ask' ? 'A few things to confirm'
    : step === 'pick' ? 'Import'
    : descriptor ? `Import ${descriptor.label.toLowerCase()}`
    : 'Import';

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={working ? undefined : onClose} maxWidth={620}>
      <div className="modal-header">
        <span className="modal-title">{title}</span>
        <button className="close-btn" onClick={onClose} disabled={working}>×</button>
      </div>

      <div className="modal-body">
        <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} />

        {/* ── STEP 0 · what are we importing (M1) ───────────────────────── */}
        {step === 'pick' && (
          <>
            <div style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
              What is in the file?
            </div>
            {available.map(d => <TypeCard key={d.key} descriptor={d} onPick={pickType} />)}
          </>
        )}

        {/* ── STEP 1 · choose ───────────────────────────────────────────── */}
        {step === 'choose' && descriptor && (
          <>
            <input
              ref={fileRef} type="file"
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={e => readFile(e.target.files?.[0])}
            />
            <div
              onClick={() => !loadingRef && fileRef.current?.click()}
              style={{
                border: '2px dashed var(--gray-ln, #E3E3DF)', borderRadius: 9,
                padding: isMobile ? '20px 12px' : '28px 16px', textAlign: 'center',
                background: 'var(--off-white, #F5F5F3)', cursor: loadingRef ? 'default' : 'pointer',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {loadingRef ? 'Reading what Stede already has…' : isMobile ? 'Choose a CSV' : 'Choose a CSV file'}
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 5 }}>
                Up to {IMPORT_HARD_CAP.toLocaleString()} rows
              </div>
            </div>

            {/* #465. Two files answer two different needs, and conflating them
                is what the old copy did: it offered Export as the answer to
                everything, which is no answer at all when the catalog is empty
                and there is nothing to export. */}
            <div style={{
              marginTop: 14, padding: '11px 13px', borderRadius: 8,
              border: '1px solid var(--gray-ln, #E3E3DF)', fontSize: 12.5, lineHeight: 1.55,
            }}>
              {descriptor.chooseHelp.map((h, i) => (
                <div key={i} style={{ marginTop: i === 0 ? 0 : 13 }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>{h.title}</div>
                  <div className="text-muted">{h.body}</div>
                  {h.download && (
                    <button className="btn btn-ghost" onClick={downloadBlankTemplate} style={{ marginTop: 9 }}>
                      {buildingTemplate ? 'Building…' : 'Download blank template'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {(step === 'map' || step === 'ask' || step === 'preview') && (
          <EncodingWarning damage={encodingDamage} />
        )}

        {/* ── STEP 2 · mapping, only when something was not understood ──── */}
        {step === 'map' && headerInfo && (
          <>
            <div style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
              <strong>{headerInfo.matchedCount} of {headerInfo.totalColumns} columns
              matched automatically.</strong>{' '}
              {headerInfo.unmatched.length === 1 ? 'One needs a home.' : `${headerInfo.unmatched.length} need a home.`}
            </div>

            {/* M21 / D13. The phone never has to do this one. */}
            {isMobile ? (
              <div style={{
                padding: '11px 13px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5,
                background: '#FDF6E7', border: '1px solid #F0C040', color: '#633806',
              }}>
                This file uses column headings Stede does not recognize, and matching them up is a
                job for a bigger screen. Open this on a computer to finish the import — or start
                from the blank template, whose headings are understood as they come.
              </div>
            ) : (
              <>
                {headerInfo.unmatched.map(u => (
                  <div key={u.index} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                    padding: '8px 10px', marginBottom: 6, borderRadius: 7,
                    border: '1px solid var(--gray-ln, #E3E3DF)', borderLeft: '3px solid #BA7517',
                    background: '#FDF6E7',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>“{u.header}”</div>
                    <select
                      value={manualMap[u.index] || ''}
                      onChange={e => setManualMap(m => ({ ...m, [u.index]: e.target.value }))}
                      style={{ fontSize: 16, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--gray-ln, #E3E3DF)' }}
                    >
                      <option value="">Ignore this column</option>
                      {descriptor.columns
                        .filter(c => !usedKeys.has(c.key) || manualMap[u.index] === c.key)
                        .map(c => <option key={c.key} value={c.key}>{c.header}</option>)}
                    </select>
                  </div>
                ))}
                <div className="text-muted" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
                  Anything left as “Ignore this column” is not imported. Nothing in Stede is changed by
                  a column you ignore.
                </div>
              </>
            )}
          </>
        )}

        {/* ── STEP 3 · the values Stede did not recognize (#468, M7) ────── */}
        {step === 'ask' && (
          <>
            <div style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
              <strong>
                {questions.length === 1
                  ? 'One value in that file is not one Stede knows.'
                  : `${questions.length} values in that file are not ones Stede knows.`}
              </strong>{' '}
              Answering once fixes every row that uses it.
            </div>
            {questions.map(q => (
              <ValueQuestion
                key={q.value}
                question={q}
                options={descriptor.questionOptions ? descriptor.questionOptions(q, reference) : []}
                answer={valueMap[q.value.trim().toLowerCase()]}
                canCreate={descriptor.canCreateValue ? descriptor.canCreateValue(q) : false}
                onAnswer={v => answerQuestion(q, v)}
              />
            ))}
            <div className="text-muted" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
              Nothing is added to Stede until you press Import — including anything you choose to
              add as new here.
            </div>
          </>
        )}

        {/* ── STEP 4 · the preview. This is the feature. ────────────────── */}
        {step === 'preview' && (
          <>
            <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>{fileName}</div>

            {banner && (
              <div style={{
                padding: '9px 12px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5,
                background: 'var(--off-white, #F5F5F3)', border: '1px solid var(--gray-ln, #E3E3DF)',
                marginBottom: 10,
              }}>
                <strong>{banner.title}</strong> {banner.body}
              </div>
            )}

            <StatTiles s={summary} />

            {/* D3, printed where the counts are — not in a tooltip, which
                touch never shows. */}
            <div className="text-muted" style={{ fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Blank cells are left alone.</strong> An empty
              cell on something Stede already has keeps whatever is stored — it never clears it.
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {[
                ['all', `All ${plan.length}`],
                ['create', `New ${summary.create}`],
                ['update', `Updated ${summary.update}`],
                ['problems', `Problems ${summary.error + summary.warnings}`],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  style={{
                    padding: '4px 11px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    border: `1px solid ${filter === k ? '#1D9E75' : 'var(--gray-ln, #E3E3DF)'}`,
                    background: filter === k ? '#EAF7F2' : '#fff',
                    fontWeight: filter === k ? 700 : 400,
                    color: 'var(--text-primary)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={{ maxHeight: isMobile ? 300 : 380, overflowY: 'auto' }}>
              {shown.length === 0
                ? <div className="text-muted" style={{ fontSize: 12.5, padding: '10px 0' }}>Nothing in this group.</div>
                : shown.map(e => (
                  <PlanRow key={e.lineNo} entry={e} detail={descriptor.describeRow(e)} />
                ))}
            </div>

            <div className="text-muted" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
              {summary.error > 0
                ? `${summary.error} ${summary.error === 1 ? 'row' : 'rows'} will be skipped. Nothing has been saved yet.`
                : 'Nothing has been saved yet.'}
              {' '}{descriptor.footnote}
            </div>
          </>
        )}

        {/* ── STEP 5 · the result, in the same four terms as the preview ── */}
        {step === 'result' && result && (
          <>
            {result.unitsAdded > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px',
                marginBottom: 6, borderRadius: 7, border: '1px solid var(--gray-ln, #E3E3DF)',
                borderLeft: '3px solid #BA7517',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {result.unitsAdded} new package {result.unitsAdded === 1 ? 'type' : 'types'} added
                  </div>
                  <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    They are on the list in Company Settings now, for everybody.
                  </div>
                </div>
              </div>
            )}
            <div style={{
              display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px',
              marginBottom: 6, borderRadius: 7, border: '1px solid var(--gray-ln, #E3E3DF)',
              borderLeft: '3px solid #1D9E75',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {result.created} {result.created === 1 ? noun.one : noun.many} added
                </div>
                <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>They are in Stede now.</div>
              </div>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px',
              marginBottom: 6, borderRadius: 7, border: '1px solid var(--gray-ln, #E3E3DF)',
              borderLeft: '3px solid #3C7FD0',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {result.updated} {result.updated === 1 ? noun.one : noun.many} updated
                </div>
                <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                  Each change is recorded, with your name and the time.
                </div>
              </div>
            </div>
            <div style={{
              padding: '8px 10px', marginBottom: 6, borderRadius: 7,
              border: '1px solid var(--gray-ln, #E3E3DF)', borderLeft: '3px solid #D8D8D4',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{result.unchanged} unchanged</div>
              <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                Nothing in the file differed from what Stede already had.
              </div>
            </div>
            {(result.skipped > 0 || result.failed.length > 0) && (
              <div style={{
                padding: '8px 10px', marginBottom: 6, borderRadius: 7,
                border: '1px solid #EBC4C4', borderLeft: '3px solid #A32D2D', background: '#FDF3F3',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {result.skipped + result.failed.length} {result.skipped + result.failed.length === 1 ? 'row' : 'rows'} skipped
                </div>
                <div style={{ fontSize: 11.5, marginTop: 2, color: '#5A5A5A', lineHeight: 1.45 }}>
                  {result.skipped > 0 && `Rows ${plan.filter(e => e.mode === 'error').map(e => e.lineNo).join(', ')}. Nothing was written for them.`}
                  {result.failed.map(f => (
                    <div key={f.lineNo} style={{ marginTop: 3 }}>Row {f.lineNo} ({f.name}): {f.message}</div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="modal-footer">
        {(step === 'pick' || step === 'choose') && (
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        )}
        {step === 'choose' && available.length > 1 && (
          <button className="btn btn-ghost" onClick={() => setStep('pick')}>Back</button>
        )}

        {step === 'map' && (
          <>
            <button className="btn btn-ghost" onClick={() => setStep('choose')}>Back</button>
            {!isMobile && (
              <button className="btn btn-primary" onClick={continueFromMapping}>Continue</button>
            )}
          </>
        )}

        {step === 'ask' && (
          <>
            <button className="btn btn-ghost" onClick={() => setStep('choose')}>Back</button>
            <button
              className="btn btn-primary"
              disabled={!answeredAll}
              onClick={() => goPreview(rows, headerRowIndex, activeMapping, valueMap)}
            >
              {answeredAll
                ? 'Continue to preview'
                : `${questions.length - questions.filter(q => valueMap[q.value.trim().toLowerCase()] !== undefined).length} still to answer`}
            </button>
          </>
        )}

        {step === 'preview' && (
          <>
            <button className="btn btn-ghost" onClick={onClose} disabled={working}>Cancel</button>
            {/* Callout D — the button counts what it will actually do, so its
                label and the result screen cannot disagree. */}
            <button
              className="btn btn-primary"
              onClick={doImport}
              disabled={working || summary.willWrite === 0}
            >
              {working
                ? `Importing… ${progress ? `${progress.done} of ${progress.total}` : ''}`
                : summary.willWrite === 0
                  ? 'Nothing to import'
                  : `Import ${summary.willWrite} ${summary.willWrite === 1 ? noun.one : noun.many}`}
            </button>
          </>
        )}

        {step === 'result' && (
          <>
            {plan.some(e => e.mode === 'error') && (
              <button className="btn btn-ghost" onClick={downloadRejects}>
                Download the {summary.error} skipped {summary.error === 1 ? 'row' : 'rows'}
              </button>
            )}
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </Overlay>
  );
}
